"use client";

import { useEffect, useRef, useState } from "react";

import { resolveBg } from "@/lib/bg-color";
import { formatPrice } from "@/lib/format-price";
import type { PriceStory } from "@/lib/prices";
import type { Region } from "@/lib/region";
import { resolveLink } from "@/lib/storyblok-link";

function ArrowIcon() {
  return (
    <svg
      width="17"
      height="11"
      viewBox="0 0 17 11"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M1 5.5h14.5M11 1l4.5 4.5L11 10"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

type Card = {
  uuid: string;
  name: string;
  price: string | undefined;
  description: string | undefined;
  href: string;
  target?: string;
  linkLabel: string;
};

function buildCard(
  price: PriceStory,
  region: Region,
  priceRegion: Region,
  linkLabelOverride: string | undefined,
): Card {
  const c = price.content || {};
  const baseName = c.name || price.name;
  const link = resolveLink(c.learn_more_link, region);
  const defaultLabel =
    c.learn_more_label || (c.name ? `More about ${c.name}` : "Learn more");
  return {
    uuid: price.uuid,
    name: c.asterisk ? `${baseName}${c.asterisk}` : baseName,
    price: formatPrice(
      priceRegion === "uk" ? c.price_uk : c.price_ie,
      priceRegion,
    ),
    description: c.description,
    href: link.href,
    target: link.target,
    linkLabel: linkLabelOverride || defaultLabel,
  };
}

// Pick the desktop column count based on how many cards are shown. Caps
// at 4 — anything more wraps onto a second row. 3-up kicks in at md
// (768px); 4-up holds at 2-col through tablet and only opens to 4
// at lg (1115px) so the cards don't get squashed.
function gridColsClass(n: number): string {
  if (n <= 1) return "sm:grid-cols-1";
  if (n === 2) return ""; // default sm:grid-cols-2 already covers it
  if (n === 3) return "md:grid-cols-3";
  return "lg:grid-cols-4";
}

export default function GlobalPricingView({
  title,
  intro,
  bgColor,
  prices,
  region,
  priceRegion,
  linkLabelOverride,
}: {
  title: string;
  intro: string;
  bgColor: unknown;
  prices: PriceStory[];
  region: Region;
  priceRegion: Region;
  linkLabelOverride: string | undefined;
}) {
  const { bg, onDark } = resolveBg(bgColor, "forest-03");
  const scrollerRef = useRef<HTMLUListElement>(null);
  const cardRefs = useRef<(HTMLLIElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isScrollable, setIsScrollable] = useState(false);

  // Drop cards that have no price for the current region.
  const cards = prices
    .map((p) => buildCard(p, region, priceRegion, linkLabelOverride))
    .filter((c) => Boolean(c.price));

  // Mobile slider: track which card is centred so the dot pagination
  // mirrors what the user is looking at. Skip on sm+ where the list is
  // a static grid.
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const update = () => {
      const rect = scroller.getBoundingClientRect();
      const center = rect.left + rect.width / 2;
      let closest = 0;
      let minDist = Infinity;
      cardRefs.current.forEach((card, i) => {
        if (!card) return;
        const cRect = card.getBoundingClientRect();
        const cCenter = cRect.left + cRect.width / 2;
        const dist = Math.abs(cCenter - center);
        if (dist < minDist) {
          minDist = dist;
          closest = i;
        }
      });
      const maxScroll = scroller.scrollWidth - scroller.clientWidth;
      if (scroller.scrollLeft <= 1) closest = 0;
      else if (scroller.scrollLeft >= maxScroll - 1) closest = cards.length - 1;
      setActiveIndex(closest);
      setIsScrollable(scroller.scrollWidth > scroller.clientWidth + 4);
    };
    update();
    scroller.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      scroller.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [cards.length]);

  if (cards.length === 0) return null;

  const cols = gridColsClass(cards.length);
  const headingColor = onDark ? "text-white" : "text-purple-05";
  const introColor = onDark ? "text-white/85" : "text-black/65";

  return (
    <section className={bg}>
      <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] py-[clamp(3rem,5vw,4.5rem)]">
        {(title || intro) && (
          <div className="flex max-w-[640px] flex-col gap-3">
            {title && (
              <h2
                className={`text-[clamp(2.25rem,4vw,3rem)] leading-[1.08] font-bold ${headingColor}`}
              >
                {title}
              </h2>
            )}
            {intro && (
              <p className={`text-body leading-[1.6] ${introColor}`}>
                {intro}
              </p>
            )}
          </div>
        )}

        <ul
          ref={scrollerRef}
          className={`mt-8 flex snap-x snap-mandatory gap-5 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] sm:grid sm:snap-none sm:grid-cols-2 sm:gap-5 sm:overflow-visible [&::-webkit-scrollbar]:hidden ${cols} ${
            title || intro ? "md:mt-12" : ""
          }`}
        >
          {cards.map((card, i) => (
            <PriceCard
              key={card.uuid}
              card={card}
              cardRef={(el) => {
                cardRefs.current[i] = el;
              }}
            />
          ))}
        </ul>

        {isScrollable && cards.length > 1 && (
          <div className="mt-6 flex justify-center sm:hidden">
            <div className="flex items-center">
              {cards.map((card, i) => (
                <button
                  key={card.uuid}
                  type="button"
                  onClick={() => {
                    cardRefs.current[i]?.scrollIntoView({
                      behavior: "smooth",
                      inline: "center",
                      block: "nearest",
                    });
                  }}
                  aria-label={`Go to price ${i + 1}`}
                  className="flex h-6 min-w-6 cursor-pointer items-center justify-center"
                >
                  <span
                    aria-hidden
                    className={`block h-1.5 rounded-full transition-all duration-300 ${
                      activeIndex === i
                        ? "w-8 bg-purple-03"
                        : onDark
                          ? "w-2 bg-white/40"
                          : "w-2 bg-purple-01"
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function PriceCard({
  card,
  cardRef,
}: {
  card: Card;
  cardRef: (el: HTMLLIElement | null) => void;
}) {
  const hasLink = card.href !== "#";
  return (
    <li
      ref={cardRef}
      className="w-[82%] shrink-0 snap-start sm:w-auto"
    >
      <div className="flex h-full flex-col gap-5 rounded-[16px] bg-white p-6 lg:rounded-[20px] lg:p-7">
        <div className="flex flex-col gap-3">
          <p className="text-body leading-[1.2] font-bold text-black">
            {card.name}
          </p>
          {card.price && (
            <p className="text-purple-03 text-[28px] leading-[1.054] font-extrabold tracking-[0.02em]">
              {card.price}
            </p>
          )}
          {card.description && (
            <p className="text-body leading-[1.4] text-[#595959]">
              {card.description}
            </p>
          )}
        </div>
        {hasLink && (
          <a
            href={card.href}
            {...(card.target ? { target: card.target } : {})}
            rel={card.target === "_blank" ? "noopener noreferrer" : undefined}
            className="text-purple-03 mt-auto inline-flex w-fit items-center gap-[7px] py-1 text-body leading-[1.054] font-extrabold hover:underline"
          >
            {card.linkLabel}
            <ArrowIcon />
          </a>
        )}
      </div>
    </li>
  );
}
